import React, { useContext, useEffect, useRef, useState } from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import Button from 'components/button/Button';

import {
  ROUTE_SETTINGS_USERS_ADD,
  ROUTE_SETTINGS,
  ROUTE_SETTINGS_USERS_USER
} from 'routes/RouteList';
import { QUERY_GET_USERS_BY_ENTITY_ID } from 'services/aws/user-query';
import Entity from 'models/Entity';
import Panel from 'components/panel/Panel';
import PanelHeader from 'components/panel/PanelHeader';
import PanelTitleWrapper from 'components/panel/panel-title/PanelTitleWrapper';
import PanelTitle from 'components/panel/panel-title/PanelTitle';
import PanelSubtitle from 'components/panel/panel-title/PanelSubtitle';
import { FormattedMessage } from 'react-intl';
import messages from 'messages';
import Icon from 'components/icon/Icon';
import PanelTools from 'components/panel/PanelTools';
import { sortUsers } from 'utils/sort';
import Breadcrumbs from 'components/breadcrumbs/Breadcrumbs';
import Breadcrumb from 'components/breadcrumbs/Breadcrumb';
import Loader from 'components/loader/Loader';
import { StoreContext } from 'index';
import { useQuery } from '@apollo/client';
import MessageText from 'components/message/MessageText';
import Message from 'components/message/Message';
import User from 'models/User';
import VirtualizedEntityList from 'containers/pages/sporters/VirtualizedEntityList';
import { useAbility } from '@casl/react';
import { AbilityContext } from 'Can';
import usePersonStatus from 'hooks/utils/usePersonStatus';
import { getFilteredUsers } from 'contexts/GroupsContext';
import Person from 'models/Person';

const Users = ({ userId }) => {
  const ability = useAbility(AbilityContext);
  const { push } = useHistory();
  const {
    authStore: {
      user: { rootEntityId }
    }
  } = useContext(StoreContext);
  const { personStatusHandler } = usePersonStatus(rootEntityId);

  const usersRef = useRef([]);
  const [users, setUsers] = useState([]);

  const { loading, error, data } = useQuery(QUERY_GET_USERS_BY_ENTITY_ID, {
    variables: { id: rootEntityId }
  });

  useEffect(() => {
    if (data?.getEntity) {
      const entity = new Entity(data.getEntity);
      usersRef.current = sortUsers(
        entity.users.map(user => new User({ ...user, filtered: false }))
      );
      setUsers(usersRef.current);
    }
  }, [data]);

  const filterUsers = filter => {
    const filteredUsers = getFilteredUsers(
      sortUsers(usersRef.current),
      filter.search ?? ''
    );

    // get a list of all users but not the ones that are in filteredUsers
    const usersNotInFilteredUsers = usersRef.current
      .filter(
        client =>
          !filteredUsers.some(filteredUser => filteredUser.id === client.id)
      )
      .map(user => new Person({ ...user, filtered: false }));

    setUsers([...filteredUsers, ...usersNotInFilteredUsers]);
  };

  return (
    <Panel>
      <PanelHeader borderBottom extraClassNames="u-margin-bottom-none">
        <Breadcrumbs>
          <Breadcrumb onClick={() => push(ROUTE_SETTINGS)}>
            <FormattedMessage {...messages.breadcrumbSettings} />
          </Breadcrumb>
        </Breadcrumbs>
        <PanelTitleWrapper>
          <PanelTitle>
            <FormattedMessage {...messages.titleUsers} />
          </PanelTitle>
          <PanelSubtitle>
            <FormattedMessage {...messages.subtitleUsers} />
          </PanelSubtitle>
        </PanelTitleWrapper>
        <PanelTools extraClassNames="u-margin-bottom-none">
          {ability.can('create', 'Users') && (
            <Button
              rounded
              primary
              onClick={() => push(ROUTE_SETTINGS_USERS_ADD)}
            >
              <Icon id="add-white" />
              <FormattedMessage {...messages.buttonUsersAddUser} />
            </Button>
          )}
        </PanelTools>
      </PanelHeader>
      {loading && <Loader />}
      {error && <p>Error! {error.message}</p>}
      {!loading && (
        <>
          {users.length > 0 ? (
            <VirtualizedEntityList
              id={'settings'}
              items={users}
              activeId={userId}
              enableSelectAll={false}
              extraClassNames="u-padding-top-medium"
              showPersonStatus
              onClick={person => {
                push(
                  generatePath(ROUTE_SETTINGS_USERS_USER, {
                    userId: person.id
                  })
                );
              }}
              onFilter={filterUsers}
              onClickPersonStatus={personStatusHandler}
            />
          ) : (
            <Message emptyState icon="sporters">
              <MessageText>
                <FormattedMessage {...messages.noUsersAddedMessage} />
              </MessageText>
            </Message>
          )}
        </>
      )}
    </Panel>
  );
};

export default Users;
